import type { Sprite } from 'pixi.js';
import type { BossDef } from './boss-base';

/**
 * C6 — The Quartet (phase flavor; def + tuning, behaviour in quartet.ts).
 * Four clone ships hold a rotating formation around the arena quarters and
 * fire SYNCHRONIZED volleys. Each clone has its OWN health (docs/12 L1).
 * PHASE PAIRS (docs/02 B1): only one opposite pair is SOLID at a time — the
 * other pair is a see-through phase ghost that shots pass through; the
 * pairs swap after a flicker. Two survivors enrage and both stay solid.
 */
export const QUARTET: BossDef = {
  id: 'quartet',
  nameKey: 'boss.quartet',
  tier: 2,
  minRoom: 1,
  baseHp: 1350,
  radius: 30, // per-clone radius — custom collision overrides
  bodyKey: 'boss/quartet/clone',
  movement: 'orbit', // unused — fully custom movement
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Phase echo: the clone nearest the player pulses a ring
      tier: 3,
      pattern: {
        type: 'radial', count: 10, interval: 5.0, spinDeg: 18,
        bullet: 'bullet/enemy/ring', speed: 135, radius: 10, damage: 2,
      },
    },
  ],
};

export const CLONE_COUNT = 4;
export const CLONE_RADIUS = 30;
export const ORBIT_R = 350;
export const ORBIT_R_ENRAGED = 240;
export const ORBIT_SPEED = 0.42; // rad/s
export const BREATHE_AMP = 55; // formation radius pulse
export const BREATHE_SPEED = 0.8;
export const VOLLEY_INTERVAL = 3.4; // synchronized aimed crossfire
export const RING_INTERVAL = 8.5; // tangent arc sprays (closing circle)
export const ENRAGE_FIRE_MUL = 1.45;
export const ENRAGE_SPEED = 1.35;
// In-fiction telegraphs (X13): clones blink before the crossfire and
// swell before the closing ring — seconds of warning
export const VOLLEY_WARN = 0.5;
export const RING_WARN = 0.6;
export const WARN_TINT = 0xffa080;
export const RING_SWELL = 1.12;
/** Tier that widens the crossfire to a 3-fan before the enrage */
export const FAN_TIER = 2;
export const FAN_ONE: ReadonlyArray<number> = [0];
export const FAN_THREE: ReadonlyArray<number> = [-0.18, 0, 0.18];
export const SHOT = { volley: 250, ring: 125 } as const;

export interface Clone {
  alive: boolean;
  hp: number;
  angle: number; // formation slot offset
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  sprite: Sprite;
}

/** Phase pairs: clones 0+2 vs 1+3 trade solidity (docs/02 B1) */
export const PHASE = {
  swap: 3.4,
  /** Both pairs flicker this long before the swap */
  warn: 0.5,
  ghostAlpha: 0.28,
  ghostTint: 0xc8b8ff,
  /** Phasing needs at least this many clones alive */
  minAlive: 3,
} as const;
